'use client';

import { Experience } from "@/types/career";
import { useState } from "react";
import { ModalShell } from "./model-shell";
import { Field } from "./field";
import { Err } from "./err";

const uid = () => Math.random().toString(36).slice(2, 9);

const inputClass =
  "w-full global-border-radius bg-white border border-brand-bg/[0.1] px-4 py-3 text-sm text-brand-bg " +
  "placeholder-brand-bg/30 focus:outline-none focus:border-brand-bg/25 transition-all duration-200";

export function ExperienceModal({
  initial,
  onSave,
  onClose,
}: {
  initial?: Experience;
  onSave: (e: Experience) => void;
  onClose: () => void;
}) {
  const [data, setData] = useState<Experience>(
    initial ?? { id: uid(), company: "", position: "", start_date: "", end_date: "" }
  );
  const [isPresent, setIsPresent] = useState(initial?.end_date === "present");
  const [errors, setErrors] = useState<Partial<Experience>>({});

  const set = (k: keyof Experience, v: string) => {
    setData((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e: Partial<Experience> = {};
    if (!data.company.trim())              e.company    = "Required";
    if (!data.position.trim())             e.position   = "Required";
    if (!data.start_date)                  e.start_date = "Required";
    if (!isPresent && !data.end_date)      e.end_date   = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({ ...data, end_date: isPresent ? "present" : data.end_date });
  };

  return (
    <ModalShell title={initial ? "Edit Experience" : "Add Experience"} onClose={onClose}>
      <div className="space-y-4">
        <Field label="Company" required>
          <input value={data.company} onChange={(e) => set("company", e.target.value)}
            placeholder="Kansala Corp" className={inputClass} />
          {errors.company && <Err>{errors.company}</Err>}
        </Field>
        <Field label="Position" required>
          <input value={data.position} onChange={(e) => set("position", e.target.value)}
            placeholder="Senior Engineer" className={inputClass} />
          {errors.position && <Err>{errors.position}</Err>}
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Start date" required>
            <input type="date" value={data.start_date} onChange={(e) => set("start_date", e.target.value)}
              className={inputClass + " [color-scheme:light]"} />
            {errors.start_date && <Err>{errors.start_date}</Err>}
          </Field>
          <Field label="End date" required>
            <input type="date" value={isPresent ? "" : data.end_date}
              disabled={isPresent}
              onChange={(e) => set("end_date", e.target.value)}
              className={inputClass + " [color-scheme:light] disabled:opacity-40 disabled:cursor-not-allowed"} />
            {errors.end_date && <Err>{errors.end_date}</Err>}
          </Field>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={isPresent} onChange={(e) => setIsPresent(e.target.checked)}
            className="rounded accent-brand-bg" />
          <span className="text-sm text-brand-bg/60">I currently work here</span>
        </label>
      </div>
      <div className="flex gap-3 mt-6">
        <button onClick={onClose}
          className="flex-1 rounded-full border border-brand-bg/15 py-3 text-sm font-semibold text-brand-bg/55 hover:bg-brand-bg/[0.05] transition-all">
          Cancel
        </button>
        <button onClick={handleSave}
          className="flex-1 rounded-full bg-brand-grey py-3 text-sm font-semibold text-brand-card transition-all 3xl:text-base">
          Save
        </button>
      </div>
    </ModalShell>
  );
}
